import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Box,
    ButtonGroup,
    IconButton,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { MouseEvent, useState } from 'react';
import PricingTemplateModal from './PricingTemplateModal';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import {
    AddCircle,
    Delete,
    Edit,
    KeyboardArrowDown,
    KeyboardArrowUp,
} from '@mui/icons-material';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import {
    useGetPricingTemplatesQuery,
    useReorderPricingTemplatesMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { PricingTemplate } from '../../../types/contractor';
import TemplatePreviewModal from './TemplatePreviewModal';
import DeleteModal from './DeleteModal';
import GenericTable from '../../../shared/GenericTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import toast from '../../../shared/Toast';
import invariant from 'tiny-invariant';

const PricingTemplates = () => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const { data: templates, isLoading } = useGetPricingTemplatesQuery(
        loggedInUser?.company,
        { skip: !loggedInUser?.company },
    );

    const [reorderTemplates] = useReorderPricingTemplatesMutation();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] =
        useState<PricingTemplate | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const handleDelete = async (
        e: MouseEvent<HTMLButtonElement>,
        template: PricingTemplate,
    ) => {
        e.stopPropagation();
        setSelectedTemplate(template);
        setDeleteOpen(true);
    };

    const handleEdit = (
        e: MouseEvent<HTMLButtonElement>,
        template: PricingTemplate,
    ) => {
        e.stopPropagation();
        setSelectedTemplate(template);
        setModalOpen(true);
    };

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            enableSorting: false,
            accessorFn: (row) => `${row.name}`,
        },
        {
            accessorKey: 'items',
            header: 'Items',
            enableSorting: false,
            size: 280,
            maxSize: 280,
            accessorFn: (row) =>
                `${row.items?.map((item: any) => item.name).join(', ')}`,
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <CustomTooltip title={value}>
                        <Typography
                            variant='body3'
                            sx={{
                                display: 'block',
                                maxWidth: 280,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {value}
                        </Typography>
                    </CustomTooltip>
                );
            },
        },
        {
            accessorKey: 'display',
            header: 'Visible',
            enableSorting: false,
            accessorFn: (row) => (row.display ? 'Yes' : 'No'),
        },
    ];

    const handleSwap = async (a: any, b: any) => {
        try {
            invariant(templates && loggedInUser);

            const updated = [...templates];
            const [moved] = updated.splice(a.index, 1);
            updated.splice(b.index, 0, moved);

            const order = updated
                .map(({ id }) => id)
                .filter((id): id is number => id !== undefined);

            await reorderTemplates([loggedInUser?.company, { order }]).unwrap();
        } catch {
            toast.error('Error reordering pricing templates');
        }
    };

    return (
        <>
            <Typography variant='h1'>Pricing Templates</Typography>
            <Box
                sx={{ display: 'flex', justifyContent: 'space-between', py: 2 }}
            >
                <Typography variant='body3' sx={{ color: 'text.primary' }}>
                    Manage pricing templates to communicate service costs to
                    hosts.
                </Typography>
                {isMobile || isIpad ? (
                    <CustomIconButton
                        variant='contained'
                        color='primary'
                        onClick={() => setModalOpen(true)}
                    >
                        <AddCircle />
                    </CustomIconButton>
                ) : (
                    <CustomButton
                        variant='contained'
                        color='primary'
                        onClick={() => setModalOpen(true)}
                    >
                        New Template
                    </CustomButton>
                )}
            </Box>
            <GenericTable
                columns={columns}
                data={templates ?? []}
                tableName='Pricing-Templates'
                loading={isLoading}
                enableRowOrdering
                enableRowSelection={false}
                renderTopToolbar={<></>}
                enableRowActions
                positionActionsColumn='last'
                renderRowActions={({ row, table }) => {
                    return (
                        <ButtonGroup size='small'>
                            <CustomTooltip title='Edit Template'>
                                <IconButton
                                    size='small'
                                    color='primary'
                                    onClick={(e) => handleEdit(e, row.original)}
                                >
                                    <Edit />
                                </IconButton>
                            </CustomTooltip>
                            <CustomTooltip title='Delete Template'>
                                <IconButton
                                    size='small'
                                    color='error'
                                    onClick={(e) =>
                                        handleDelete(e, row.original)
                                    }
                                >
                                    <Delete />
                                </IconButton>
                            </CustomTooltip>
                            {isMobile && (
                                <>
                                    <CustomTooltip title='Move Up'>
                                        <div>
                                            <IconButton
                                                size='small'
                                                disabled={row.index === 0}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSwap(
                                                        row,
                                                        table.getRow(
                                                            `${+row.id - 1}`,
                                                        ),
                                                    );
                                                }}
                                            >
                                                <KeyboardArrowUp />
                                            </IconButton>
                                        </div>
                                    </CustomTooltip>
                                    <CustomTooltip title='Move Down'>
                                        <div>
                                            <IconButton
                                                size='small'
                                                disabled={
                                                    !!templates &&
                                                    row.index ===
                                                        templates.length - 1
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSwap(
                                                        row,
                                                        table.getRow(
                                                            `${+row.id + 1}`,
                                                        ),
                                                    );
                                                }}
                                            >
                                                <KeyboardArrowDown />
                                            </IconButton>
                                        </div>
                                    </CustomTooltip>
                                </>
                            )}
                        </ButtonGroup>
                    );
                }}
                onRowClick={(row) => {
                    setSelectedTemplate(row);
                    setPreviewOpen(true);
                }}
                mantineRowDragHandleProps={({ table }) => ({
                    onDragEnd: () => {
                        const { draggingRow, hoveredRow } = table.getState();

                        if (
                            !draggingRow ||
                            !hoveredRow ||
                            !('original' in hoveredRow)
                        )
                            return;

                        handleSwap(draggingRow, hoveredRow);
                    },
                })}
            />
            <PricingTemplateModal
                template={selectedTemplate}
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedTemplate(null);
                }}
                length={templates?.length ?? 0}
            />
            <TemplatePreviewModal
                template={selectedTemplate}
                open={previewOpen}
                onClose={() => {
                    setPreviewOpen(false);
                    setSelectedTemplate(null);
                }}
                handleEdit={() => {
                    setPreviewOpen(false);
                    setModalOpen(true);
                }}
            />
            <DeleteModal
                open={deleteOpen}
                onClose={() => {
                    setDeleteOpen(false);
                    setSelectedTemplate(null);
                }}
                template={selectedTemplate}
            />
        </>
    );
};

export default PricingTemplates;
